import React, { useState } from "react";
import { Modal, Form, Input, Icon, notification, Avatar, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import countries from "i18n-iso-countries";
import styled from "styled-components";
import { useSelector } from "react-redux";

import urlConnection from "../../../../../assets/urlConnection.svg";
import Button from "../../../../../components/Button";
import { httpToHttps } from "../../../../../utils/images";
import Multi from "../../../../../components/inputs/Multi";

const Title = styled.p`
  margin: 0px;
  font-size: 22px;
  margin-top: 10px;
  font-weight: bold;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0px;
  font-size: 16px;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  margin-bottom: 20px;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
`;

const CustomAvatar = styled(Avatar)`
  border: 1px solid #e5e9ed;
`;

const ConnectionImg = styled.img`
  width: 64px;
  margin-left: 5px;
  margin-right: 5px;
`;

const Label = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme["@gray-60"]};
`;

const Content = styled.div`
  width: 180px;
  display: flex;
  justify-content: center;
`;

// url validation regex came from here https://www.regextester.com/94502
// eslint-disable-next-line no-useless-escape
const urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;

const IntegrationUrl = ({
  form,
  visible = true,
  onClose,
  loading,
  type,
  setScrapper,
  removeScrapper,
  scrapper
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("init");
  const { getFieldDecorator } = form;
  const provider = `${type.name} ${
    type.country === "INT" ? "" : countries.getName(type.country, "es")
  }`.trim();
  const company = useSelector(({ company }) => company);

  const InfoAvatars = () => (
    <AvatarContainer>
      <CustomAvatar size={76} src={httpToHttps(company.logo)} />
      <ConnectionImg src={urlConnection} alt="Link" />
      <CustomAvatar size={76} src={httpToHttps(type.logo)} />
    </AvatarContainer>
  );

  return (
    <Modal
      width={570}
      visible={visible}
      destroyOnClose={true}
      onCancel={() => !loading && onClose()}
      maskClosable={false}
      closable={!loading}
      keyboard={false}
      footer={null}
    >
      {step === "init" ? (
        <div>
          <Title>
            {t("opportunity:wizard.multiposting.integrations.integrateUrl", {
              provider
            })}
          </Title>
          <InfoAvatars />
          <Subtitle>
            {t("opportunity:wizard.multiposting.integrations.copyPaste")}
          </Subtitle>
          <Subtitle>
            <strong>{provider}</strong>
          </Subtitle>
          <Form
            layout="horizontal"
            style={{ width: 370, margin: "auto", marginTop: 40 }}
          >
            <Form.Item
              name="url"
              colon={false}
              label={
                <span>
                  <Label>
                    {t(
                      "opportunity:wizard.multiposting.integrations.insertUrlTitle"
                    )}
                  </Label>
                  <Tooltip
                    title={
                      <div style={{ display: "flex" }}>
                        <Content>
                          <Icon
                            style={{ fontSize: 20, marginTop: 10 }}
                            type="info-circle"
                          />
                        </Content>
                        <div style={{ padding: 5 }}>
                          <div>
                            {t(
                              "opportunity:wizard.multiposting.integrations.tooltipUrl",
                              {
                                provider
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <Icon
                      type="info-circle"
                      style={{
                        marginLeft: 5,
                        color: "#3D64FF",
                        fontSize: 16,
                        cursor: "pointer"
                      }}
                    />
                  </Tooltip>
                </span>
              }
            >
              {getFieldDecorator("url", {
                rules: [
                  {
                    validator: (_, value, callback) => {
                      if (R.isEmpty(value) || R.isEmpty(value.trim())) {
                        return callback();
                      }
                      if (!value.match(urlPattern)) {
                        return callback(t("form:urlValidation"));
                      }
                      return callback();
                    }
                  }
                ],
                initialValue: R.pathOr("", [type.provider, "url"], scrapper)
              })(
                <Input
                  autoFocus
                  allowClear
                  onPressEnter={e => e.preventDefault()}
                  data-test="integration-url-input"
                  prefix={
                    <Icon type="link" style={{ color: "rgba(0,0,0,.25)" }} />
                  }
                />
              )}
            </Form.Item>
            <Form.Item name="otherUrls">
              {getFieldDecorator("otherUrls", {
                rules: [
                  {
                    validator: (_, items, callback) => {
                      if (Array.isArray(items) && items.length > 0) {
                        items.forEach(value => {
                          if (R.isEmpty(value) || R.isEmpty(value.trim())) {
                            return callback(t("form:emptyFieldValidation"));
                          }
                          if (!value.match(urlPattern)) {
                            return callback(t("form:urlValidation"));
                          }
                        });
                      }
                      return callback();
                    }
                  }
                ],
                initialValue: R.pathOr(
                  [],
                  [type.provider, "otherUrls"],
                  scrapper
                )
              })(
                <Multi emptyValue="" selector={e => e.target.value}>
                  <Input
                    autoFocus
                    allowClear
                    onPressEnter={e => e.preventDefault()}
                    prefix={
                      <Icon type="link" style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                  />
                </Multi>
              )}
            </Form.Item>
          </Form>
          <WrapperButton>
            <Button
              type="quaternary"
              disabled={loading}
              onClick={onClose}
              size="lg"
            >
              {t("global:cancel")}
            </Button>
            <Button
              size="lg"
              data-test="discard-button"
              loading={loading}
              type="primary"
              onClick={ev => {
                ev.preventDefault();
                form.validateFields(async (err, values) => {
                  if (!err) {
                    if (values.url) {
                      setScrapper(
                        R.set(R.lensProp([type.provider]), values, scrapper)
                      );
                      setStep("resolved");
                    } else {
                      setScrapper(R.omit([type.provider], scrapper));
                      removeScrapper(type.provider);
                      onClose();
                    }
                    notification.warning({
                      message: t("global:alert"),
                      description: t("global:rememberSave")
                    });
                  }
                });
              }}
            >
              {t("global:integrate")}
            </Button>
          </WrapperButton>
        </div>
      ) : (
        <div>
          <Title>
            {t("opportunity:wizard.multiposting.integrations.doneIntegration", {
              provider
            })}
          </Title>
          <InfoAvatars />
          <Subtitle>
            {`${t(
              "opportunity:wizard.multiposting.integrations.doneIntegrationText"
            )}`}{" "}
            <strong>{provider}</strong>
          </Subtitle>
          <WrapperButton>
            <Button
              type="primary"
              onClick={onClose}
              size="lg"
              data-test="ready-button"
            >
              {t("global:ready")}
            </Button>
          </WrapperButton>
        </div>
      )}
    </Modal>
  );
};

export default Form.create()(IntegrationUrl);
